import type {
  Asset as ProductionAsset,
  Episode,
  Person as ProductionPerson,
  Project,
  ProjectConfig,
  Season,
  Sequence,
  Shot as ProductionShot,
  Task,
  TimeLog,
  Version,
} from "../models/production";
import type { Asset, AssetRateCard, BidVersion, Person, ProjectDocument, ProjectSetup, RateCard, Shot } from "../types";
import type { FptTaskSyncRecord } from "../utils/fpt/projectRecordCompat";
import { createMissingSequencesEpisodes, backfillShotHierarchyFromShotCodes } from "../utils/migrations/productionHierarchy";
import { getShotCodeParserConfig } from "../shared/shotCodeParser";
import type { HierarchySnapshot } from "../shared/rollupTypes";
import { recomputeHierarchyRollups } from "../shared/productionHierarchy";
import { buildEntitySyncMetadataMap, createIdentityMapping } from "./fptSyncMetadata";
import { mapTaskType } from "./taskTypeMapper";

function isoNow() {
  return new Date().toISOString();
}

function hasPatchDifferences<T extends Record<string, unknown>>(entity: T, patch: Partial<T>) {
  return Object.entries(patch).some(([key, value]) => entity[key as keyof T] !== value);
}

function mergeById<T extends { id: string }>(existing: T[], derived: T[]) {
  const merged = new Map(existing.map((item) => [item.id, { ...item }]));
  for (const item of derived) {
    const current = merged.get(item.id);
    merged.set(item.id, current ? { ...item, ...current } : { ...item });
  }
  return Array.from(merged.values());
}

function buildTaskId(parentId: string, taskType: string) {
  return `${parentId}:task:${taskType}`;
}

function buildServerSafeTaskTitle(taskType: string) {
  return mapTaskType(taskType).contentLabel || "task";
}

function sumTaskDays<T extends object>(values: T | undefined) {
  if (!values) return 0;
  return Object.values(values as Record<string, number | undefined>).reduce((acc, value) => acc + (value || 0), 0);
}

function sumShotProductionTaskDays(shot: Shot) {
  return (
    Number(shot.days?.concept || 0) +
    Number(shot.days?.dmp || 0) +
    Number(shot.days?.comp || 0) +
    Number(shot.days?.roto || 0) +
    Number(shot.days?.prep || 0) +
    Number(shot.days?.gfx || 0) +
    Number(shot.days?.cameraTR || 0) +
    Number(shot.days?.objectTR || 0) +
    Number(shot.days?.rANIM || 0) +
    Number(shot.days?.layout || 0) +
    Number(shot.days?.anim || 0) +
    Number(shot.days?.light || 0) +
    Number(shot.days?.fx || 0)
  );
}

function sumAssetProductionTaskDays(asset: Asset) {
  return (
    Number(asset.days?.concept || 0) +
    Number(asset.days?.dmp || 0) +
    Number(asset.days?.model || 0) +
    Number(asset.days?.rig || 0) +
    Number(asset.days?.retop || 0) +
    Number(asset.days?.cloth || 0) +
    Number(asset.days?.fur || 0) +
    Number(asset.days?.groom || 0) +
    Number(asset.days?.texture || 0) +
    Number(asset.days?.anim || 0) +
    Number(asset.days?.fx || 0) +
    Number(asset.days?.lookdev || 0) +
    Number(asset.days?.photoscan || 0)
  );
}

function resolveShotSupervisionDays(shot: Shot, supervisionPercentage: number) {
  const quantity = Number(shot.quantity || 1);
  const explicitDays = Number(shot.days?.supervisor || 0);
  if (explicitDays > 0) {
    return explicitDays * quantity;
  }

  const productionTaskDays = sumShotProductionTaskDays(shot);
  return productionTaskDays * (supervisionPercentage / 100) * quantity;
}

function resolveAssetSupervisionDays(asset: Asset, supervisionPercentage: number) {
  const quantity = Number(asset.quantity || 1);
  const explicitDays = Number(asset.days?.supervisor || 0);
  if (explicitDays > 0) {
    return explicitDays * quantity;
  }

  const productionTaskDays = sumAssetProductionTaskDays(asset);
  return productionTaskDays * (supervisionPercentage / 100) * quantity;
}

function buildProjectTask(
  projectId: string,
  taskType: string,
  title: string,
  description: string,
  pipelineStep: string,
  setup: ProjectSetup,
  taskMappings?: Record<string, FptTaskSyncRecord>,
  bidHours = 0,
  blendedDailyRate?: number
): Task {
  const now = isoNow();
  const taskId = buildTaskId(projectId, taskType);
  const existingMapping = taskMappings?.[taskId];

  return {
    id: taskId,
    projectId,
    parentEntityType: "project",
    parentEntityId: projectId,
    taskType,
    title,
    description,
    pipelineStep,
    blendedDailyRate,
    bidHours,
    actualHours: 0,
    varianceHours: 0 - bidHours,
    progressPercent: 0,
    awardStatus: setup.awardStatus,
    progressStatus: setup.progressStatusSummary,
    assignedArtistIds: setup.personIds || [],
    versionIds: [],
    fptTaskId: existingMapping?.fptTaskId ? String(existingMapping.fptTaskId) : undefined,
    createdAt: now,
    updatedAt: now,
  };
}

function buildProjectLevelTasks(
  projectId: string,
  setup: ProjectSetup,
  shots: Shot[],
  assets: Asset[],
  taskMappings?: Record<string, FptTaskSyncRecord>,
  rates?: Partial<RateCard> | null,
  assetRates?: Partial<AssetRateCard> | null
): Task[] {
  const supervisionPercentage = Number(setup.supervisionPercentage || 0);
  const shotSupervisionDays = shots.reduce((sum, shot) => sum + resolveShotSupervisionDays(shot, supervisionPercentage), 0);
  const assetSupervisionDays = assets.reduce((sum, asset) => sum + resolveAssetSupervisionDays(asset, supervisionPercentage), 0);
  const combinedSupervisionDays = shotSupervisionDays + assetSupervisionDays;

  const shotSupervisionCost = shotSupervisionDays * Number(rates?.supervisor || 0);
  const assetSupervisionCost = assetSupervisionDays * Number(assetRates?.supervisor || 0);
  const blendedDailyRate =
    shotSupervisionCost + assetSupervisionCost > 0
      ? Number((((shotSupervisionCost + assetSupervisionCost) / combinedSupervisionDays) || 0).toFixed(2))
      : undefined;

  return [
    buildProjectTask(
      projectId,
      "supervisor",
      "Supervision",
      "Combined supervision from shot Sup and asset Sup columns. Onset supervision is tracked separately and excluded.",
      "Supervision",
      setup,
      taskMappings,
      combinedSupervisionDays,
      blendedDailyRate
    ),
    buildProjectTask(
      projectId,
      "production",
      "Production",
      "Project-level production logging bucket for operational time in FPT. No bid amount is sent from the app.",
      "Production",
      setup,
      taskMappings
    ),
    buildProjectTask(
      projectId,
      "editorial",
      "Editorial",
      "Project-level editorial logging bucket for operational time in FPT. No bid amount is sent from the app.",
      "Editorial",
      setup,
      taskMappings
    ),
  ];
}

function createLegacyTasksFromShots(projectId: string, shots: Shot[], taskMappings?: Record<string, FptTaskSyncRecord>): Task[] {
  const now = isoNow();
  return shots.flatMap((shot) =>
    Object.entries(shot.days || {})
      .filter(([, days]) => (days || 0) > 0)
      .map(([taskType, days]) => {
        const taskId = buildTaskId(shot.id, taskType);
        const existingMapping = taskMappings?.[taskId];
        return {
        id: taskId,
        projectId,
        seasonId: shot.seasonId,
        episodeId: shot.episodeId,
        sequenceId: shot.sequenceId,
        sceneId: shot.sceneId,
        parentEntityType: "shot" as const,
        parentEntityId: shot.id,
        shotId: shot.id,
        taskType,
        title: buildServerSafeTaskTitle(taskType),
        bidHours: Number(days || 0),
        actualHours: 0,
        varianceHours: 0 - Number(days || 0),
        progressPercent: shot.progressPercent || 0,
        awardStatus: shot.awardStatus,
        progressStatus: shot.progressStatus,
        assignedArtistIds: shot.assignedArtistIds || [],
        versionIds: shot.versionIds || [],
        fptTaskId: existingMapping?.fptTaskId ? String(existingMapping.fptTaskId) : undefined,
        createdAt: now,
        updatedAt: now,
      };
    })
  );
}

function createLegacyTasksFromAssets(projectId: string, assets: Asset[], taskMappings?: Record<string, FptTaskSyncRecord>): Task[] {
  const now = isoNow();
  return assets.flatMap((asset) =>
    Object.entries(asset.days || {})
      .filter(([, days]) => (days || 0) > 0)
      .map(([taskType, days]) => {
        const taskId = buildTaskId(asset.id, taskType);
        const existingMapping = taskMappings?.[taskId];
        return {
        id: taskId,
        projectId,
        seasonId: asset.seasonId,
        episodeId: asset.episodeId,
        sequenceId: asset.sequenceId,
        sceneId: asset.sceneId,
        parentEntityType: "asset" as const,
        parentEntityId: asset.id,
        assetId: asset.id,
        taskType,
        title: buildServerSafeTaskTitle(taskType),
        bidHours: Number(days || 0),
        actualHours: 0,
        varianceHours: 0 - Number(days || 0),
        progressPercent: asset.progressPercent || 0,
        awardStatus: asset.awardStatus,
        progressStatus: asset.progressStatus,
        assignedArtistIds: asset.assignedArtistIds || [],
        versionIds: asset.versionIds || [],
        fptTaskId: existingMapping?.fptTaskId ? String(existingMapping.fptTaskId) : undefined,
        createdAt: now,
        updatedAt: now,
      };
    })
  );
}

export function buildProjectConfigFromSetup(projectId: string, setup: ProjectSetup): ProjectConfig {
  const now = isoNow();
  return {
    id: setup.projectConfigId || `${projectId}:config:default`,
    projectId,
    shotCodePattern: setup.shotCodePattern,
    shotCodeBreadcrumb: setup.shotCodeBreadcrumb,
    statusConfigId: setup.statusConfigId,
    shotCodeParserConfig: getShotCodeParserConfig(null),
    createdAt: now,
    updatedAt: now,
  };
}

export function buildHierarchySnapshotFromLegacyProject(args: {
  projectId: string;
  setup: ProjectSetup;
  shots: Shot[];
  assets: Asset[];
  versions?: BidVersion[];
  documents?: ProjectDocument[];
  projectConfig?: Partial<ProjectConfig> | null;
  existingSeasons?: Season[];
  existingEpisodes?: Episode[];
  existingSequences?: Sequence[];
  existingScenes?: import("../models/production").Scene[];
  existingProjectTasks?: Task[];
  existingTaskMappings?: Record<string, FptTaskSyncRecord>;
  people?: Person[] | ProductionPerson[];
  timeLogs?: TimeLog[];
  rates?: Partial<RateCard> | null;
  assetRates?: Partial<AssetRateCard> | null;
}) {
  const now = isoNow();
  const projectConfig = args.projectConfig || buildProjectConfigFromSetup(args.projectId, args.setup);
  const hierarchyBackfill = backfillShotHierarchyFromShotCodes(args.projectId, args.shots, {
    shotCodeParserConfig: projectConfig.shotCodeParserConfig,
  });

  const legacyShots = args.shots.map((shot) => {
    const patch = hierarchyBackfill.updates.find((item) => item.shotId === shot.id)?.patch || {};
    return {
      ...shot,
      ...patch,
      projectId: args.projectId,
      bidHoursTotal: shot.bidHoursTotal ?? sumTaskDays(shot.days),
      actualHoursTotal: shot.actualHoursTotal ?? 0,
      varianceHours: shot.varianceHours ?? 0 - sumTaskDays(shot.days),
    };
  });

  const legacyAssets = args.assets.map((asset) => ({
    ...asset,
    projectId: args.projectId,
    bidHoursTotal: asset.bidHoursTotal ?? sumTaskDays(asset.days),
    actualHoursTotal: asset.actualHoursTotal ?? 0,
    varianceHours: asset.varianceHours ?? 0 - sumTaskDays(asset.days),
  }));

  const missing = createMissingSequencesEpisodes(args.projectId, legacyShots, {
    shotCodeParserConfig: projectConfig.shotCodeParserConfig,
  });

  const seasons = [...(args.existingSeasons || []), ...missing.seasons].filter(
    (season, index, array) => array.findIndex((item) => item.id === season.id) === index
  );
  const episodes = [...(args.existingEpisodes || []), ...missing.episodes].filter(
    (episode, index, array) => array.findIndex((item) => item.id === episode.id) === index
  );
  const sequences = [...(args.existingSequences || []), ...missing.sequences].filter(
    (sequence, index, array) => array.findIndex((item) => item.id === sequence.id) === index
  );
  const scenes = (args.existingScenes || []).filter(
    (scene, index, array) => array.findIndex((item) => item.id === scene.id) === index
  );

  const derivedProjectTaskIds = new Set([
    buildTaskId(args.projectId, "supervisor"),
    buildTaskId(args.projectId, "production"),
    buildTaskId(args.projectId, "editorial"),
  ]);
  const persistedProjectTasks = (args.existingProjectTasks || []).filter((task) => !derivedProjectTaskIds.has(task.id));
  const tasks = [
    ...createLegacyTasksFromShots(args.projectId, legacyShots, args.existingTaskMappings),
    ...createLegacyTasksFromAssets(args.projectId, legacyAssets, args.existingTaskMappings),
    ...buildProjectLevelTasks(
      args.projectId,
      args.setup,
      legacyShots,
      legacyAssets,
      args.existingTaskMappings,
      args.rates,
      args.assetRates
    ),
    ...persistedProjectTasks,
  ];
  const entitySyncMetadata = buildEntitySyncMetadataMap(legacyShots, legacyAssets);
  const shots: ProductionShot[] = legacyShots.map((shot) => ({
    id: shot.id,
    projectId: args.projectId,
    seasonId: shot.seasonId,
    episodeId: shot.episodeId,
    sequenceId: shot.sequenceId,
    sceneId: shot.sceneId,
    shotCode: shot.shotCode,
    parsedShotCode: shot.parsedShotCode,
    shotNumber: shot.shotNumber,
    title: shot.title,
    description: shot.description,
    turnoverNotes: shot.turnoverNotes,
    bidHoursTotal: shot.bidHoursTotal,
    actualHoursTotal: shot.actualHoursTotal,
    varianceHours: shot.varianceHours,
    progressPercent: shot.progressPercent,
    awardStatus: shot.awardStatus,
    progressStatus: shot.progressStatus,
    assignedArtistIds: shot.assignedArtistIds,
    taskIds: shot.taskIds,
    versionIds: shot.versionIds,
    assetIds: shot.assetIds,
    fptShotId: shot.fptShotId,
    lastTimelogAt: shot.lastTimelogAt,
    lastFptSyncAt: shot.lastFptSyncAt,
    fptSyncHash: shot.fptSyncHash,
    syncMetadata: entitySyncMetadata[shot.id],
    createdAt: now,
    updatedAt: now,
  }));
  const assets: ProductionAsset[] = legacyAssets.map((asset) => ({
    id: asset.id,
    projectId: args.projectId,
    seasonId: asset.seasonId,
    episodeId: asset.episodeId,
    sequenceId: asset.sequenceId,
    relatedSceneIds: asset.relatedSceneIds,
    relatedShotIds: asset.relatedShotIds,
    assetCode: asset.assetCode,
    assetName: asset.assetCode,
    assetType: asset.type,
    description: asset.description,
    bidHoursTotal: asset.bidHoursTotal,
    actualHoursTotal: asset.actualHoursTotal,
    varianceHours: asset.varianceHours,
    progressPercent: asset.progressPercent,
    awardStatus: asset.awardStatus,
    progressStatus: asset.progressStatus,
    taskIds: asset.taskIds,
    versionIds: asset.versionIds,
    assignedArtistIds: asset.assignedArtistIds,
    fptAssetId: asset.fptAssetId,
    lastTimelogAt: asset.lastTimelogAt,
    lastFptSyncAt: asset.lastFptSyncAt,
    fptSyncHash: asset.fptSyncHash,
    syncMetadata: entitySyncMetadata[asset.id],
    createdAt: now,
    updatedAt: now,
  }));
  const versions: Version[] = (args.versions || []).map((version) => ({
    id: version.id,
    projectId: version.projectId,
    seasonId: version.seasonId,
    episodeId: version.episodeId,
    sequenceId: version.sequenceId,
    shotId: version.shotId,
    assetId: version.assetId,
    taskId: version.taskId,
    versionCode: version.versionCode,
    versionNumber: version.versionNumber,
    description: version.versionName,
    reviewStatus: version.reviewStatus,
    submissionDate: version.exportedAt,
    notes: version.scopeType,
    createdAt: version.createdAt,
    updatedAt: version.exportedAt || version.createdAt,
  }));
  const project: Project = {
    id: args.projectId,
    title: args.setup.projectName,
    showCode: args.setup.showCode,
    client: args.setup.clientName,
    formatType: args.setup.bidStatus,
    shotCodePattern: args.setup.shotCodePattern,
    shotCodeBreadcrumb: args.setup.shotCodeBreadcrumb,
    statusConfigId: args.setup.statusConfigId,
    activeSeasonNumbers: seasons.map((season) => season.seasonNumber),
    awardStatus: args.setup.awardStatus,
    progressStatusSummary: args.setup.progressStatusSummary,
    bidHoursTotal: tasks.reduce((acc, task) => acc + (task.bidHours || 0), 0),
    actualHoursTotal: 0,
    varianceHours: 0 - tasks.reduce((acc, task) => acc + (task.bidHours || 0), 0),
    progressPercent: 0,
    shotCount: legacyShots.length,
    sequenceCount: sequences.length,
    episodeCount: episodes.length,
    taskCount: tasks.length,
    versionCount: (args.versions || []).length,
    assetCount: legacyAssets.length,
    entitySyncMetadata,
    identityMappings: Object.fromEntries(
      ((args.people || []) as Array<Person | ProductionPerson>).map((person) => [person.id, createIdentityMapping(person as Person)])
    ),
    createdAt: now,
    updatedAt: now,
  };

  return {
    snapshot: {
      projects: [project],
      seasons,
      episodes,
      sequences,
      scenes,
      shots,
      assets,
      tasks,
      versions,
      people: ((args.people || []) as Array<Person | ProductionPerson>).map((person) => ({
        id: person.id,
        googleWorkspaceUserId: "googleWorkspaceUserId" in person ? person.googleWorkspaceUserId : undefined,
        email: "email" in person ? person.email : undefined,
        fullName:
          "fullName" in person && person.fullName
            ? person.fullName
            : "name" in person
              ? person.name
              : undefined,
        firstName: "firstName" in person ? person.firstName : undefined,
        lastName: "lastName" in person ? person.lastName : undefined,
        displayName:
          "displayName" in person && person.displayName
            ? person.displayName
            : "name" in person
              ? person.name
              : undefined,
        department: "department" in person ? person.department : undefined,
        role:
          "role" in person && person.role
            ? person.role
            : "roleTitle" in person
              ? person.roleTitle
              : undefined,
        employmentType: "employmentStatus" in person ? person.employmentStatus : undefined,
        active: "active" in person ? person.active : undefined,
        fptPersonId: "fptPersonId" in person ? person.fptPersonId : undefined,
        fptUserLinked: "fptUserLinked" in person ? person.fptUserLinked : Boolean("fptPersonId" in person ? person.fptPersonId : undefined),
        identitySource: "identitySource" in person ? person.identitySource : "app",
        lastSyncedAt: "lastSyncedAt" in person ? person.lastSyncedAt : undefined,
        skillTags: "skillTags" in person ? person.skillTags : undefined,
        capacityHoursPerWeek: "capacityHoursPerWeek" in person ? person.capacityHoursPerWeek : undefined,
        hrProfileRef: "hrProfileRef" in person ? person.hrProfileRef : undefined,
        fptContractId: "fptContractId" in person ? person.fptContractId : undefined,
        contractStartDate: "contractStartDate" in person ? person.contractStartDate : undefined,
        contractEndDate: "contractEndDate" in person ? person.contractEndDate : undefined,
        contractType: "contractType" in person ? person.contractType : undefined,
        availabilityStatus: "availabilityStatus" in person ? person.availabilityStatus : undefined,
        contractSource: "contractSource" in person ? person.contractSource : undefined,
        createdAt: "createdAt" in person ? person.createdAt : now,
        updatedAt: "updatedAt" in person ? person.updatedAt : now,
      })),
      timeLogs: args.timeLogs || [],
    } satisfies HierarchySnapshot,
    projectConfig,
    invalidShots: hierarchyBackfill.invalidShots,
  };
}

export function materializeLegacyProjectHierarchy(args: {
  projectId: string;
  setup: ProjectSetup;
  shots: Shot[];
  existingSeasons?: Season[];
  existingEpisodes?: Episode[];
  existingSequences?: Sequence[];
}) {
  const projectConfig = buildProjectConfigFromSetup(args.projectId, args.setup);
  const hierarchyBackfill = backfillShotHierarchyFromShotCodes(args.projectId, args.shots, {
    shotCodeParserConfig: projectConfig.shotCodeParserConfig,
  });

  let changed = false;
  const shots = args.shots.map((shot) => {
    const patch = hierarchyBackfill.updates.find((item) => item.shotId === shot.id)?.patch || {};
    if (hasPatchDifferences(shot as unknown as Record<string, unknown>, patch as Partial<Record<string, unknown>>)) {
      changed = true;
    }
    return {
      ...shot,
      ...patch,
    };
  });

  const missing = createMissingSequencesEpisodes(args.projectId, shots, {
    shotCodeParserConfig: projectConfig.shotCodeParserConfig,
  });

  const seasons = mergeById(args.existingSeasons || [], missing.seasons);
  const episodes = mergeById(args.existingEpisodes || [], missing.episodes);
  const sequences = mergeById(args.existingSequences || [], missing.sequences);

  if ((args.existingSeasons || []).length !== seasons.length) changed = true;
  if ((args.existingEpisodes || []).length !== episodes.length) changed = true;
  if ((args.existingSequences || []).length !== sequences.length) changed = true;

  return {
    shots,
    seasons,
    episodes,
    sequences,
    invalidShots: hierarchyBackfill.invalidShots,
    changed,
  };
}

export function computeHierarchyRollupsForLegacyProject(args: {
  projectId: string;
  setup: ProjectSetup;
  shots: Shot[];
  assets: Asset[];
  versions?: BidVersion[];
  projectConfig?: Partial<ProjectConfig> | null;
  documents?: ProjectDocument[];
}) {
  const { snapshot, projectConfig, invalidShots } = buildHierarchySnapshotFromLegacyProject(args);
  return {
    projectConfig,
    invalidShots,
    snapshot,
    rollups: recomputeHierarchyRollups(args.projectId, snapshot),
  };
}
